import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import {
  X,
  Key,
  Eye,
  EyeOff,
  Copy,
  Check,
  Printer,
  Download,
  User,
  AlertTriangle,
} from 'lucide-react';
import type { StudentUser } from '@/services/userService';

interface StudentCredentialsModalProps {
  student: StudentUser;
  onClose: () => void;
}

/**
 * Modal to display student login credentials (username + temp password)
 * Used in StudentManagementPage for students who:
 * - Have a temp password (created without email)
 * - Have never logged in (lastLoginAt is null)
 */
export const StudentCredentialsModal: React.FC<StudentCredentialsModalProps> = ({
  student,
  onClose,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [copiedField, setCopiedField] = useState<'username' | 'email' | 'password' | null>(null);
  const [showSecurityWarning, setShowSecurityWarning] = useState(false);
  const [pendingAction, setPendingAction] = useState<'print' | 'download' | null>(null);

  const studentName = `${student.firstName} ${student.lastName}`;
  const username = student.studentCode || student.username || '';
  const email = student.email || '';
  const tempPassword = student.tempPassword || '';

  // Copy to clipboard
  const copyToClipboard = async (text: string, field: 'username' | 'email' | 'password') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Print credentials
  const printCredentials = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Identifiants - ${studentName}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 40px; }
              .header { text-align: center; margin-bottom: 30px; }
              .card { border: 1px solid #ccc; padding: 20px; border-radius: 8px; max-width: 400px; margin: 0 auto; }
              .field { margin-bottom: 15px; }
              .label { font-weight: bold; color: #666; font-size: 12px; text-transform: uppercase; }
              .value { font-size: 18px; font-family: monospace; background: #f5f5f5; padding: 8px; border-radius: 4px; margin-top: 4px; }
              .footer { text-align: center; margin-top: 30px; color: #999; font-size: 12px; }
            </style>
          </head>
          <body>
            <div class="header">
              <h2>Identifiants de connexion</h2>
              <p>${studentName}</p>
            </div>
            <div class="card">
              <div class="field">
                <div class="label">Identifiant (Code étudiant)</div>
                <div class="value">${username}</div>
              </div>
              ${email ? `<div class="field">
                <div class="label">Email (identifiant alternatif)</div>
                <div class="value">${email}</div>
              </div>` : ''}
              <div class="field">
                <div class="label">Mot de passe temporaire</div>
                <div class="value">${tempPassword}</div>
              </div>
            </div>
            <div class="footer">
              <p>Ce mot de passe doit être changé lors de la première connexion.</p>
              <p>Plateforme NEXEDU</p>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  // Download credentials as text file
  const downloadCredentials = () => {
    const content = `Identifiants de connexion - ${studentName}
==========================================

Identifiant (Code étudiant): ${username}${email ? `\nEmail (identifiant alternatif): ${email}` : ''}
Mot de passe temporaire: ${tempPassword}

Note: Ce mot de passe doit être changé lors de la première connexion.

---
Plateforme NEXEDU
`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `identifiants_${username}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Security confirmation handlers
  const handlePrintClick = () => {
    setShowSecurityWarning(true);
    setPendingAction('print');
  };

  const handleDownloadClick = () => {
    setShowSecurityWarning(true);
    setPendingAction('download');
  };

  const confirmAction = () => {
    if (pendingAction === 'print') printCredentials();
    else if (pendingAction === 'download') downloadCredentials();
    setShowSecurityWarning(false);
    setPendingAction(null);
  };

  const cancelSecurityWarning = () => {
    setShowSecurityWarning(false);
    setPendingAction(null);
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md overflow-hidden shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-amber-50 to-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Key className="text-amber-600" size={24} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Identifiants de connexion</h2>
              <p className="text-sm text-gray-500">{studentName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Student info */}
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg mb-4">
            <div className="p-2 bg-gray-200 rounded-full">
              <User className="text-gray-600" size={20} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">{studentName}</p>
              <p className="text-xs text-gray-500">
                {student.filiereInscrit} - Niveau {student.niveauFormation}
              </p>
            </div>
          </div>

          {/* Info message */}
          <p className="text-sm text-gray-600 mb-4">
            {email
              ? "L'étudiant peut se connecter avec son code étudiant ou son adresse email:"
              : "Cet étudiant n'a pas d'adresse email. Partagez ces identifiants pour qu'il puisse se connecter:"}
          </p>

          {/* Username field */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Identifiant (Code étudiant)
            </label>
            <div className="flex items-center gap-2">
              <div className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg font-mono text-sm">
                {username}
              </div>
              <button
                onClick={() => copyToClipboard(username, 'username')}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="Copier l'identifiant"
              >
                {copiedField === 'username' ? (
                  <Check className="text-green-600" size={20} />
                ) : (
                  <Copy size={20} />
                )}
              </button>
            </div>
          </div>

          {/* Email field (alternative login identifier) */}
          {email && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email (identifiant alternatif)
              </label>
              <div className="flex items-center gap-2">
                <div className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg font-mono text-sm truncate">
                  {email}
                </div>
                <button
                  onClick={() => copyToClipboard(email, 'email')}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Copier l'email"
                >
                  {copiedField === 'email' ? (
                    <Check className="text-green-600" size={20} />
                  ) : (
                    <Copy size={20} />
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Password field */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mot de passe temporaire
            </label>
            <div className="flex items-center gap-2">
              <div className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg font-mono text-sm">
                {showPassword ? tempPassword : '••••••••••••'}
              </div>
              <button
                onClick={() => setShowPassword(!showPassword)}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title={showPassword ? 'Masquer' : 'Afficher'}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
              <button
                onClick={() => copyToClipboard(tempPassword, 'password')}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="Copier le mot de passe"
              >
                {copiedField === 'password' ? (
                  <Check className="text-green-600" size={20} />
                ) : (
                  <Copy size={20} />
                )}
              </button>
            </div>
          </div>

          {/* Note */}
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-6">
            <p className="text-sm text-amber-800">
              <strong>Note:</strong> L'étudiant devra changer son mot de passe lors de sa première connexion.
              Une fois connecté, ces identifiants ne seront plus visibles ici.
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={handlePrintClick}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Printer size={18} />
              Imprimer
            </button>
            <button
              onClick={handleDownloadClick}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download size={18} />
              Telecharger
            </button>
          </div>
        </div>

        {/* Security Warning Dialog */}
        {showSecurityWarning && (
          <div className="fixed inset-0 z-[60] bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white p-6 rounded-xl shadow-xl max-w-md w-full">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 p-2 bg-amber-100 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-amber-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Attention - Donnees sensibles</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Ce fichier contient des identifiants confidentiels. Assurez-vous de le stocker de maniere securisee et de ne pas le partager par email non chiffre.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={cancelSecurityWarning}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={confirmAction}
                  className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
                >
                  Continuer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default StudentCredentialsModal;
